import type { NodePropertySchema } from '../../../types/node-properties';

// Starter template definitions
// Uses standard interpolation syntax:
// - {{ $timestamp }} - Current timestamp (UTC)
// - {{ $node["NodeName"].output.field }} - Reference specific field from a node
// NOTE: Only simple property paths work - NO JavaScript expressions
const STARTER_TEMPLATES: Record<string, string> = {
  basic: `<!-- Basic Template -->
<!-- Drag fields from the Input panel or use {{ $node["NodeName"].output.field }} -->
<div class="document">
    <h1>Report</h1>
    <p class="timestamp">Generated: {{ $timestamp }}</p>
    <div class="content">
        <p>Replace this with your data fields</p>
    </div>
</div>`,

  report: `<!-- Report Template -->
<!-- Drag fields from the Input panel to insert references -->
<div class="report">
    <header class="report-header">Company Report</header>
    <h1 class="report-title">Monthly Summary</h1>
    <p class="timestamp">Generated: {{ $timestamp }}</p>
    <div class="report-content">
        <p>Replace this with your data fields</p>
    </div>
    <footer class="report-footer">Confidential</footer>
</div>`,

  invoice: `<!-- Invoice Template -->
<!-- Replace placeholders with {{ $node["NodeName"].output.field }} references -->
<div class="invoice">
    <div class="invoice-header">
        <h1>INVOICE</h1>
        <p class="invoice-date">Date: {{ $timestamp }}</p>
    </div>

    <div class="bill-to">
        <h3>Bill To:</h3>
        <p>Customer Name</p>
        <p>Customer Address</p>
        <p>Customer Email</p>
    </div>

    <div class="invoice-content">
        <p>Add line items here</p>
    </div>

    <p class="invoice-notes">Thank you for your business!</p>
</div>`,

  receipt: `<!-- Receipt Template -->
<div class="receipt">
    <div class="receipt-header">
        <h1>RECEIPT</h1>
        <p class="receipt-date">{{ $timestamp }}</p>
    </div>

    <div class="receipt-content">
        <p>Add receipt items here</p>
    </div>

    <p class="thank-you">Thank you for your business!</p>
</div>`,

  table: `<!-- Table Template -->
<!-- Build your table structure, then drag fields into the cells -->
<div class="table-document">
    <h2>Data Export</h2>
    <p class="timestamp">Generated: {{ $timestamp }}</p>
    <table class="data-table">
        <thead>
            <tr>
                <th>Column 1</th>
                <th>Column 2</th>
            </tr>
        </thead>
        <tbody>
            <tr>
                <td>Value 1</td>
                <td>Value 2</td>
            </tr>
        </tbody>
    </table>
</div>`,

  email: `<!-- Email/Letter Template -->
<!-- Replace placeholders with {{ $node["NodeName"].output.field }} references -->
<div class="email-document">
    <div class="email-header">
        <p class="date">{{ $timestamp }}</p>
    </div>

    <p class="greeting">Dear Recipient,</p>

    <div class="email-body">
        <p>Your message content here</p>
    </div>

    <div class="email-closing">
        <p>Best regards,</p>
        <p class="signature">Your Name</p>
    </div>
</div>`,

  certificate: `<!-- Certificate Template -->
<!-- Replace placeholder text with {{ $node["NodeName"].output.field }} references -->
<div class="certificate">
    <div class="certificate-border">
        <div class="certificate-content">
            <h1 class="certificate-title">Certificate of Achievement</h1>
            <p class="presented-to">This is to certify that</p>
            <h2 class="recipient-name">RECIPIENT NAME</h2>
            <p class="achievement">has successfully completed the requirements</p>
            <p class="date-issued">Issued on {{ $timestamp }}</p>
            <div class="signatures">
                <div class="signature-block">
                    <div class="signature-line"></div>
                    <p>Authorized Signature</p>
                </div>
            </div>
        </div>
    </div>
</div>`,
};

const schema: NodePropertySchema = {
  nodeType: 'html-template',
  version: '1.0.0',
  properties: [
    {
      key: 'outputType',
      label: 'Output Type',
      type: 'options',
      required: false,
      default: 'html',
      description: 'Choose output type - Email mode avoids JavaScript and unsupported features',
      options: [
        { value: 'html', name: 'HTML (Web/PDF)' },
        { value: 'email', name: 'Email' },
      ],
    },
    {
      key: 'starterTemplate',
      label: 'Load Starter Template',
      type: 'options',
      required: false,
      default: 'basic',
      description: 'Select a template to load into the editor',
      options: [
        { value: 'basic', name: 'Basic' },
        { value: 'report', name: 'Report' },
        { value: 'invoice', name: 'Invoice' },
        { value: 'receipt', name: 'Receipt' },
        { value: 'table', name: 'Table Only' },
        { value: 'email', name: 'Email/Letter' },
        { value: 'certificate', name: 'Certificate' },
      ],
      updatesField: {
        targetField: 'template',
        valueMap: STARTER_TEMPLATES,
      },
    } as any,
    {
      key: 'template',
      label: 'HTML Template',
      type: 'string',
      required: true,
      default: STARTER_TEMPLATES.basic,
      description: 'Edit the HTML. Use {{ $timestamp }} for time, {{ $node["Name"].output.field }} for data fields.',
      typeOptions: {
        editor: 'code',
        editorLanguage: 'html',
      },
    },
    {
      key: 'primaryColor',
      label: 'Primary Color',
      type: 'string',
      default: '#2563eb',
      placeholder: '#2563eb',
      description: 'Accent color for headings and table headers',
    },
    {
      key: 'customCss',
      label: 'Custom CSS',
      type: 'text',
      required: false,
      default: '',
      placeholder: '.custom-class { color: red; }',
      description: 'Additional CSS styles',
      typeOptions: {
        rows: 4,
      },
    },
    {
      key: 'dateFormat',
      label: 'Date Format',
      type: 'string',
      default: 'yyyy-MM-dd',
      placeholder: 'yyyy-MM-dd',
      description: 'Date format preference (for AI template generation)',
    },
    {
      key: 'currencyCode',
      label: 'Currency',
      type: 'options',
      default: 'USD',
      options: [
        { value: 'USD', name: 'USD ($)' },
        { value: 'EUR', name: 'EUR (€)' },
        { value: 'GBP', name: 'GBP (£)' },
        { value: 'CAD', name: 'CAD ($)' },
        { value: 'AUD', name: 'AUD ($)' },
        { value: 'JPY', name: 'JPY (¥)' },
      ],
      description: 'Currency preference (for AI template generation)',
    },
  ],
};

export { STARTER_TEMPLATES };
export default schema;
